import {OrderManagementProcessProvider} from '@hconnect/tuc-processes/orderManagement/OrderManagementProcessProvider'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {useFeaturesState} from '../../../../Organisms/Features'
import {Order} from '../../../../Pages/Order/Order.types'
import {ErrorView} from '../../shared/order-editor/components/ErrorView'
import {LoadingView} from '../../shared/order-editor/components/LoadingView'
import {useOrderChangeOptions} from '../hooks/useOrderChangeOptions'
import {mapHubOrderToOrderDto} from '../utils/mapHubOrderToOrderDto'

import {OrderManagementEditorContainer} from './editors/OrderManagementEditorContainer'
import {PlusLoadEditorContainer} from './editors/PlusLoadEditorContainer'

export type OrderManagementMode = 'default' | 'plusLoad'

interface OrderManagementProps {
  selectedOrder: Order
  onClose: () => void
  jobSiteInfo?: string
  mode?: OrderManagementMode
}

/**
 * OrderManagement Component
 *
 * Main entry point for order management functionality.
 * Wraps the editor container with OrderManagementProcessProvider to provide
 * form state management, validation, and change tracking.
 *
 * This component handles:
 * - Fetching order change options from the API
 * - Setting up the form context with the fetched options
 * - Providing initial order data to the form
 * - Managing the lifecycle of order editing
 */
export const OrderManagement = ({
  selectedOrder,
  onClose,
  jobSiteInfo,
  mode = 'default'
}: OrderManagementProps) => {
  const {t} = useTranslation()
  const {getFeature} = useFeaturesState()

  const {
    data: orderChangeOptions,
    isLoading,
    error,
    refetch
  } = useOrderChangeOptions(selectedOrder.orderId, selectedOrder.checkSum)

  const order = useMemo(() => mapHubOrderToOrderDto(selectedOrder), [selectedOrder])

  const featureToggles = useMemo(
    () => ({
      enableConfirmationPin: getFeature('enableConfirmationPin'),
      enableSlotManagementForRmcOrderManagement: getFeature(
        'enableSlotManagementForRmcOrderManagement'
      )
    }),
    [getFeature]
  )

  if (isLoading) {
    return (
      <LoadingView
        message={t('orderIntake.rmc.orderManagement.loadingOptions', 'Loading order options...')}
        data-test-id="order-options-loading"
      />
    )
  }

  if (error || !orderChangeOptions) {
    return (
      <ErrorView
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        onClose={onClose}
        data-test-id="order-options-error"
      />
    )
  }

  return (
    <OrderManagementProcessProvider
      key={order.orderId}
      orderChangeOptions={orderChangeOptions}
      order={order}
      featureToggles={featureToggles}
      respectStartedOrderSpacingRestrictions
    >
      {mode === 'plusLoad' ? (
        <PlusLoadEditorContainer onClose={onClose} jobSiteInfo={jobSiteInfo} />
      ) : (
        <OrderManagementEditorContainer onClose={onClose} jobSiteInfo={jobSiteInfo} />
      )}
    </OrderManagementProcessProvider>
  )
}
